/**
 * Metric-based unlock rules for achievements (bireysel + takım başarımları).
 *
 * Mirrors the season application-rule DSL (`TSeasonApplicationRule` + its
 * catalog) so the two condition editors read as one system: a rule picks a
 * *metric* from a catalog, optionally scopes it to a question *category*, then
 * compares it against a threshold with an operator. All rules on an achievement
 * apply together (AND) — same contract the season builder states in its Alert.
 *
 * The catalogs live here (not on the backend) because both achievement modules
 * are still static/in-memory; when the real endpoints land these become the
 * fallback for a `GET /admin/achievements/metric-catalog` response — the shapes
 * already match `TSeasonApplicationRuleDefinition`.
 */

export type TAchievementUnlockOperator = 'gte' | 'lte' | 'eq' | 'between'

export type TAchievementUnlockRule = {
  id: string
  /** Catalog metric key, e.g. `category_correct_answers`. */
  metric: string
  /** Question-category scope; `null` when the metric is not category-scoped. */
  category_id: string | null
  operator: TAchievementUnlockOperator
  /** Single threshold, or `[min, max]` for the `between` operator. */
  value: number | [number, number]
}

export type TAchievementMetricDefinition = {
  metric: string
  label: string
  description: string
  /** Whether a category must be picked for this metric. */
  requires_category: boolean
  value_type: 'count' | 'percentage'
  /** Trailing unit for count metrics ("soru", "maç", …); `null` for ratios. */
  unit: string | null
  operators: readonly TAchievementUnlockOperator[]
}

export const ACHIEVEMENT_UNLOCK_OPERATOR_LABELS: Record<
  TAchievementUnlockOperator,
  string
> = {
  gte: 'En az',
  lte: 'En fazla',
  eq: 'Eşit',
  between: 'Arasında',
}

const COUNT_OPERATORS: readonly TAchievementUnlockOperator[] = [
  'gte',
  'lte',
  'eq',
  'between',
]
const RATIO_OPERATORS: readonly TAchievementUnlockOperator[] = [
  'gte',
  'lte',
  'between',
]

/** Metrics an individual (player) badge can be gated on. */
export const INDIVIDUAL_ACHIEVEMENT_METRICS: readonly TAchievementMetricDefinition[] =
  [
    {
      metric: 'category_correct_answers',
      label: 'Doğru cevap',
      description:
        'Oyuncunun seçilen kategoride verdiği doğru cevap sayısı. Örn. Tarih kategorisinde en az 10 doğru cevap.',
      requires_category: true,
      value_type: 'count',
      unit: 'soru',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'category_answered_questions',
      label: 'Cevaplanan soru',
      description:
        'Oyuncunun seçilen kategoride cevapladığı toplam soru sayısı (doğru + yanlış).',
      requires_category: true,
      value_type: 'count',
      unit: 'soru',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'category_accuracy',
      label: 'Doğruluk oranı',
      description:
        'Oyuncunun seçilen kategorideki doğru cevap yüzdesi. Düşük soru sayılarında da hesaplanır.',
      requires_category: true,
      value_type: 'percentage',
      unit: null,
      operators: RATIO_OPERATORS,
    },
    {
      metric: 'category_match_wins',
      label: 'Kazanılan maç',
      description:
        'Oyuncunun seçilen kategorinin oynandığı maçlardaki zafer sayısı.',
      requires_category: true,
      value_type: 'count',
      unit: 'maç',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'total_correct_answers',
      label: 'Toplam doğru cevap',
      description:
        'Tüm kategorilerdeki doğru cevap sayısı. Kategori seçimi gerektirmez.',
      requires_category: false,
      value_type: 'count',
      unit: 'soru',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'total_match_wins',
      label: 'Toplam kazanılan maç',
      description: 'Oyuncunun tüm oyun modlarındaki toplam zafer sayısı.',
      requires_category: false,
      value_type: 'count',
      unit: 'maç',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'total_matches_played',
      label: 'Oynanan maç',
      description: 'Oyuncunun tamamladığı toplam maç sayısı.',
      requires_category: false,
      value_type: 'count',
      unit: 'maç',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'match_win_streak',
      label: 'Üst üste kazanma serisi',
      description: 'Oyuncunun art arda kazandığı en uzun maç serisi.',
      requires_category: false,
      value_type: 'count',
      unit: 'maç',
      operators: COUNT_OPERATORS,
    },
  ]

/** Metrics a team badge can be gated on. */
export const TEAM_ACHIEVEMENT_METRICS: readonly TAchievementMetricDefinition[] =
  [
    {
      metric: 'category_correct_answers',
      label: 'Takımın doğru cevabı',
      description:
        'Takım üyelerinin seçilen kategoride verdiği doğru cevapların toplamı. Örn. Tarih kategorisinde en az 50 doğru cevap.',
      requires_category: true,
      value_type: 'count',
      unit: 'soru',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'category_accuracy',
      label: 'Doğruluk oranı',
      description: 'Takımın seçilen kategorideki doğru cevap yüzdesi.',
      requires_category: true,
      value_type: 'percentage',
      unit: null,
      operators: RATIO_OPERATORS,
    },
    {
      metric: 'category_match_wins',
      label: 'Kazanılan maç',
      description:
        'Takımın, seçilen kategorinin oynandığı maçlardaki zafer sayısı.',
      requires_category: true,
      value_type: 'count',
      unit: 'maç',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'total_correct_answers',
      label: 'Toplam doğru cevap',
      description:
        'Takımın tüm kategorilerdeki doğru cevap toplamı. Kategori seçimi gerektirmez.',
      requires_category: false,
      value_type: 'count',
      unit: 'soru',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'total_match_wins',
      label: 'Toplam kazanılan maç',
      description: 'Takımın tüm oyun modlarındaki toplam zafer sayısı.',
      requires_category: false,
      value_type: 'count',
      unit: 'maç',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'match_win_streak',
      label: 'Üst üste kazanma serisi',
      description: 'Takımın art arda kazandığı en uzun maç serisi.',
      requires_category: false,
      value_type: 'count',
      unit: 'maç',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'league_championships',
      label: 'Lig şampiyonluğu',
      description: 'Takımın birinci olarak tamamladığı lig sayısı.',
      requires_category: false,
      value_type: 'count',
      unit: 'kez',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'tournament_championships',
      label: 'Turnuva şampiyonluğu',
      description: 'Takımın kupayı kaldırdığı turnuva sayısı.',
      requires_category: false,
      value_type: 'count',
      unit: 'kez',
      operators: COUNT_OPERATORS,
    },
    {
      metric: 'season_participations',
      label: 'Katıldığı sezon',
      description: 'Takımın katılımının kabul edildiği sezon sayısı.',
      requires_category: false,
      value_type: 'count',
      unit: 'sezon',
      operators: COUNT_OPERATORS,
    },
  ]

export function findAchievementMetric(
  catalog: readonly TAchievementMetricDefinition[],
  metric: string
): TAchievementMetricDefinition | undefined {
  return catalog.find((item) => item.metric === metric)
}

/** Stable-ish local id for a freshly added rule (mirrors `season.factory`'s `uid`). */
function ruleId(): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `unlock-rule-${rand}`
}

/** A blank rule seeded from a catalog entry's first operator + sane threshold. */
export function makeAchievementUnlockRule(
  definition: TAchievementMetricDefinition
): TAchievementUnlockRule {
  const operator = definition.operators[0] ?? 'gte'
  return {
    id: ruleId(),
    metric: definition.metric,
    category_id: null,
    operator,
    value: operator === 'between' ? [1, 10] : 1,
  }
}

/**
 * Re-seeds a rule's operator/value after its metric changed — a percentage
 * metric may not support the previously picked operator, and a `between`
 * threshold has a different shape than a single number.
 */
export function retargetAchievementUnlockRule(
  rule: TAchievementUnlockRule,
  definition: TAchievementMetricDefinition
): TAchievementUnlockRule {
  const operator = definition.operators.includes(rule.operator)
    ? rule.operator
    : (definition.operators[0] ?? 'gte')
  return {
    ...rule,
    metric: definition.metric,
    category_id: definition.requires_category ? rule.category_id : null,
    operator,
    value: normalizeUnlockValue(rule.value, operator),
  }
}

/** Coerces a threshold to the shape the operator expects. */
export function normalizeUnlockValue(
  value: TAchievementUnlockRule['value'],
  operator: TAchievementUnlockOperator
): TAchievementUnlockRule['value'] {
  if (operator === 'between') {
    const pair = unlockValuePair(value)
    return [pair[0], pair[1]]
  }
  return Array.isArray(value) ? (value[0] ?? 1) : value
}

/** Reads a threshold as a `[min, max]` pair regardless of its stored shape. */
export function unlockValuePair(
  value: TAchievementUnlockRule['value']
): [number, number] {
  if (Array.isArray(value)) {
    const min = value[0] ?? 1
    const max = value[1] ?? min
    return [min, max]
  }
  return [value, value]
}

/** Reads a threshold as a single number regardless of its stored shape. */
export function unlockValueSingle(
  value: TAchievementUnlockRule['value']
): number {
  return Array.isArray(value) ? (value[0] ?? 0) : value
}

function formatAmount(
  amount: number,
  definition: TAchievementMetricDefinition
): string {
  if (definition.value_type === 'percentage') return `%${amount}`
  return definition.unit ? `${amount} ${definition.unit}` : String(amount)
}

/** "en az 10 soru", "%60 - %80 arası", "tam 3 maç" … */
export function describeUnlockThreshold(
  rule: TAchievementUnlockRule,
  definition: TAchievementMetricDefinition
): string {
  if (rule.operator === 'between') {
    const [min, max] = unlockValuePair(rule.value)
    return `${formatAmount(min, definition)} - ${formatAmount(max, definition)} arası`
  }
  const amount = formatAmount(unlockValueSingle(rule.value), definition)
  const prefix = {
    gte: 'en az',
    lte: 'en fazla',
    eq: 'tam',
  }[rule.operator]
  return `${prefix} ${amount}`
}

export type TAchievementUnlockRuleDescription = {
  /** Resolved category name, or `null` when the metric is not category-scoped. */
  category: string | null
  /** Catalog label, e.g. "Doğru cevap". */
  metric: string
  /** Threshold phrase, e.g. "en az 10 soru". */
  threshold: string
  /** One-line summary, e.g. "Tarih kategorisinde doğru cevap: en az 10 soru". */
  text: string
}

const UNKNOWN_METRIC = 'Bilinmeyen metrik'
const UNKNOWN_CATEGORY = 'Bilinmeyen kategori'

/**
 * Renders a rule as display parts. `categoryNames` maps category id → name;
 * ids missing from it (e.g. a category deleted after the rule was saved) fall
 * back to a neutral label instead of leaking a raw uuid into the UI.
 */
export function describeAchievementUnlockRule(
  rule: TAchievementUnlockRule,
  catalog: readonly TAchievementMetricDefinition[],
  categoryNames: Readonly<Record<string, string>> = {}
): TAchievementUnlockRuleDescription {
  const definition = findAchievementMetric(catalog, rule.metric)
  if (!definition) {
    return {
      category: null,
      metric: UNKNOWN_METRIC,
      threshold: '-',
      text: UNKNOWN_METRIC,
    }
  }

  const category = definition.requires_category
    ? rule.category_id
      ? (categoryNames[rule.category_id] ?? UNKNOWN_CATEGORY)
      : UNKNOWN_CATEGORY
    : null
  const threshold = describeUnlockThreshold(rule, definition)
  const scope = category ? `${category} kategorisinde ` : ''
  const metricLabel = definition.label.toLocaleLowerCase('tr')

  return {
    category,
    metric: definition.label,
    threshold,
    text: `${scope}${metricLabel}: ${threshold}`,
  }
}

/** All rules as one-line summaries — used by tables / tooltips. */
export function achievementUnlockSummary(
  rules: readonly TAchievementUnlockRule[],
  catalog: readonly TAchievementMetricDefinition[],
  categoryNames: Readonly<Record<string, string>> = {}
): string[] {
  return rules.map(
    (rule) => describeAchievementUnlockRule(rule, catalog, categoryNames).text
  )
}
